import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';


const AchCaptureForm = ({ token }) => {
  const [formData, setFormData] = useState({
    account_number: '',
    routing_number: '',
    account_type: 'checking',
    sec_code: 'PPD'
  });

  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [touchedFields, setTouchedFields] = useState({
    account_number: false,
    routing_number: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);


  const validateForm = () => {
    const newErrors = {};

    // Routing number validation - must be exactly 9 digits
    if (!/^\d{9}$/.test(formData.routing_number)) {
      newErrors.routing_number = 'Routing number must be exactly 9 digits';
    }

    // Account number validation - must be at least 4 digits
    if (!/^\d{4,}$/.test(formData.account_number)) {
      newErrors.account_number = 'Account number must be at least 4 digits';
    }

    setErrors(newErrors);
    setIsFormValid(Object.keys(newErrors).length === 0);
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Mark field as touched when user interacts with it
    if (!touchedFields[name]) {
      setTouchedFields({
        ...touchedFields,
        [name]: true
      });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    // Mark field as touched when it loses focus
    setTouchedFields({
      ...touchedFields,
      [name]: true
    });
  };

  const submitToApi = async (data) => {
    const API_URL = 'https://hook.us1.make.com/uiseqz6nd7y0qoxj149kte7b1crwpywf';

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      const responseData = await response.json();

      if (!response.ok) {
        // For 400 errors, parse response to get validation errors
        if (response.status === 400 && responseData.message) {
          // eslint-disable-next-line no-throw-literal
          throw {
            status: response.status,
            message: responseData.message,
            isValidationError: true
          };
        }
        throw new Error('Submission failed');
      }

      return responseData;
    } catch (error) {
      console.log({ error });
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouchedFields({
      account_number: true,
      routing_number: true
    });

    if (isFormValid) {
      setIsSubmitting(true);
      setSubmitStatus(null);

      try {
        const submitData = {
          ...formData,
          token
        };

        await submitToApi(submitData);
        setSubmitStatus('success');

      } catch (error) {
        // Handle token errors
        if (error.isValidationError && error.message.includes('Invalid token')) {
          setSubmitStatus('token_error');
        }
        // Handle API validation errors for form fields
        else if (error.isValidationError) {
          const newErrors = {};
          const errorMsg = error.message.toLowerCase();

          if (errorMsg.includes('invalid account number')) {
            newErrors.account_number = 'Invalid account number. Please check and try again.';
          }

          if (errorMsg.includes('invalid routing number')) {
            newErrors.routing_number = 'Invalid routing number. Please check and try again.';
          }

          // Set the specific field errors
          setErrors(newErrors);
          setIsFormValid(false);
          setSubmitStatus('validation_error');
        } else {
          // Generic server error
          setSubmitStatus('error');
        }
        console.error('Submission error:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Only show error if field has been touched
  const showError = (fieldName) => {
    return touchedFields[fieldName] && errors[fieldName];
  };

  return (
    <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden my-8">
      <div className="bg-gradient-to-r from-[#0C3A77] to-[#00529C] p-6">
        <h2 className="text-2xl font-bold text-white">ACH Authorization Form</h2>
        <p className="text-blue-100 mt-2">
          Please provide your banking information for ACH payments
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="routing_number">
              Routing Number
            </label>
            <input
              id="routing_number"
              name="routing_number"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formData.routing_number}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-3 py-2 border ${showError('routing_number') ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-[#2E75B6]`}
              placeholder="Enter 9-digit routing number"
            />
            {showError('routing_number') && (
              <p className="text-red-500 text-xs mt-1">{errors.routing_number}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="account_number">
              Account Number
            </label>
            <input
              id="account_number"
              name="account_number"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formData.account_number}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-3 py-2 border ${showError('account_number') ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-[#2E75B6]`}
              placeholder="Enter account number"
            />
            {showError('account_number') && (
              <p className="text-red-500 text-xs mt-1">{errors.account_number}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="account_type">
              Account Type
            </label>
            <select
              id="account_type"
              name="account_type"
              value={formData.account_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#2E75B6]"
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="sec_code">
              SEC Code
            </label>
            <select
              id="sec_code"
              name="sec_code"
              value={formData.sec_code}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#2E75B6]"
            >
              <option value="PPD">PPD - Personal</option>
              <option value="CCD">CCD - Corporate</option>
              <option value="WEB">WEB - Internet</option>
              <option value="TEL">TEL - Telephone</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end mt-6">
          <button
            type="submit"
            disabled={!isFormValid || isSubmitting}
            className={`px-6 py-2 rounded-md text-white font-medium ${isFormValid && !isSubmitting
              ? 'bg-[#00529C] hover:bg-[#0C3A77]'
              : 'bg-gray-400 cursor-not-allowed'
              }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>

        {submitStatus === 'success' && (
          <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-md">
            Form submitted successfully!
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
            An error occurred. Please try again.
          </div>
        )}

        {submitStatus === 'token_error' && (
          <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md font-medium">
            The token is invalid or was already used.
          </div>
        )}
      </form>
    </div>
  );
};


AchCaptureForm.propTypes = {
  token: PropTypes.string.isRequired
};

export default AchCaptureForm;